import { setUserIdentity } from '@/common/helpers/tracking-utils';
import { prefStorage } from '@samknows/utils';

import {
  isPrefStorageSetup,
  setupPreferences
} from '@/common/services/account/userPreferences';
import {
  fetchAndSaveUserSession,
  isUserSessionSetup,
  removeUserSession
} from '@/common/services/account/userSession';
import { Accessibles } from '@/common/services/account/types';
import store from '@/common/store';
import { resetSentryUserIdentity } from '@/common/services/sentryTracking';

/**
 * Removes the stored user session within this applications context
 * This has no effect on ThousandEyes session
 */
export function removeUser(): void {
  store.commit('personalDetails/RESET_STATE');
  prefStorage._purge();
  removeUserSession();
  resetSentryUserIdentity();
}

/**
 * Handle setting up the user used throughout the app.
 * A user can't access private pages until:
 * - They are authenticated via ThousandEyes and have a valid JSESSIONID Cookie
 * - User session has been fetched and saved as a Cookie to use on page reload. The user session contains all user data required by the application
 * - Pref storage has been setup in local storage. This is used to access user grants, accessibles and active view and is required for private page access
 *
 * Setting up the user is only required to be done once on App load.
 * Local storage and Cookie can be accessed on future visit
 */
export async function setupUser(): Promise<void> {
  await fetchAndSaveUserSession();

  // Set prefStorage object
  await setupPreferences();

  // Track user profile properties
  setUserIdentity();
}

export function getAccessibleUnitsList(): string[] {
  const accessibles: Accessibles = prefStorage.get('accessibles');
  return accessibles?.units?.map((item) => item.unitId as unknown as string); // TODO [eslint-fixes] this conversion looks like a bug
}

/**
 * Checks whether the user is a volunteer or not
 * Volunteer in this context means signup: e.g a user who exists as a signup in our BE
 * but has not yet been accepted to a study and doesn't have a whitebox yet
 *
 * @returns {boolean}
 */
export function isVolunteer(): boolean {
  const accessibles: Accessibles = prefStorage.get('accessibles');

  if (!accessibles || !accessibles.volunteer) {
    return false;
  }

  if (accessibles.units && accessibles.units.length) {
    return false;
  }

  return true;
}

/**
 * Check whether the user has been setup
 *
 * We consider the user to be set up on SamKnows side when:
 * - The session has been setup
 * - The pref storage has been setup
 *
 * Those are required for the app to work properly
 *
 * @returns {boolean}
 */
export function checkIsUserSetup(): boolean {
  return isUserSessionSetup() && isPrefStorageSetup();
}
